import React, { useMemo, useState } from 'react';
import '../App.css';
import { getCardLoc, getCardTypeName, getUILoc } from '../utils/cardUtils.js';
import { CardIllustration } from './Card.js';
import CardNameWithRuby from './CardNameWithRuby.js';

/**
 * カード詳細オーバーレイ
 */
const CardDetail = ({ card, cardDefs, onClose }) => {
    const [showFlavor, setShowFlavor] = useState(false);

    // 翻訳データの取得
    const cardLoc = getCardLoc(card.id);
    const cardName = cardLoc.name;
    const cardDescription = cardLoc.description;
    const cardFlavor = cardLoc.flavor || '';
    const cardTypeName = getCardTypeName(card.card_type);

    // 関連カード（トークンなど）の抽出
    const relatedCards = useMemo(() => {
        if (!card || !cardDefs) return [];
        
        const baseDef = (Array.isArray(cardDefs) ? cardDefs.find(d => d.id === card.id) : cardDefs[card.id]) || card;
        const ids = new Set();

        if (Array.isArray(baseDef.related_card_templates)) {
            baseDef.related_card_templates.forEach(id => ids.add(id));
        }

        const findCardTemplates = (obj) => {
            if (!obj || typeof obj !== 'object') return;
            if (obj.card_template_id) ids.add(obj.card_template_id);
            if (obj.card_if_above) ids.add(obj.card_if_above);
            if (obj.card_if_below) ids.add(obj.card_if_below);
            if (obj.options && Array.isArray(obj.options)) obj.options.forEach(id => ids.add(id));

            Object.values(obj).forEach(value => {
                if (Array.isArray(value)) value.forEach(item => findCardTemplates(item));
                else if (typeof value === 'object') findCardTemplates(value);
            });
        };

        if (baseDef.triggers) findCardTemplates(baseDef.triggers);

        return Array.from(ids).map(id => {
            const def = Array.isArray(cardDefs) ? cardDefs.find(d => d.id === parseInt(id)) : cardDefs[id];
            if (!def) return null;
            const loc = getCardLoc(id);
            return { 
                ...def, 
                name: loc.name, 
                description: loc.description,
                typeName: getCardTypeName(def.card_type)
            };
        }).filter(Boolean);
    }, [card, cardDefs]);

    if (!card) return null;

    const handleOverlayClick = (e) => {
        if (e.target.className === 'card-detail-overlay') {
            onClose();
        }
    };

    return (
        <div className="card-detail-overlay" onClick={handleOverlayClick}>
            <div className="card-detail-modal">
                {/* 背景イラスト演出 (ライブラリと同様) */}
                <CardIllustration cardId={card.id} isBackground={true} />

                <div className="card-detail-header">
                    <div className="card-header-main">
                        <h2>
                            <button
                                className="card-name-toggle"
                                onClick={() => setShowFlavor(!showFlavor)}
                                title={showFlavor ? "カード詳細を表示" : "フレーバーテキストを表示"}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'inherit',
                                    cursor: 'pointer',
                                    fontSize: 'inherit',
                                    fontWeight: 'inherit',
                                    margin: 0,
                                    padding: 0,
                                    borderBottom: '2px solid',
                                    borderBottomColor: showFlavor ? '#ffd700' : '#4fc3f7',
                                    paddingBottom: '2px'
                                }}
                            >
                                <CardNameWithRuby cardId={card.id} fallbackName={cardName} />
                            </button>
                        </h2>
                        {card.is_token && <span className="token-badge">TOKEN</span>}
                    </div>
                    <button className="close-button" onClick={onClose}>&times;</button>
                </div>

                <div className="card-detail-content">
                    <div className="card-detail-info">
                        <div className="info-row">
                            <span className="info-label">{getUILoc('LABELS.CARD_TYPE')}:</span>
                            <span className="info-value">{cardTypeName}</span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">{getUILoc('LABELS.REQUIRED_SCALE')}:</span>
                            <span className="info-value">{card.required_scale}</span>
                        </div>
                        {card.card_type === 1 && (
                            <div className="info-row">
                                <span className="info-label">{getUILoc('LABELS.DURABILITY')}:</span>
                                <span className="info-value">
                                    {card.current_durability !== undefined ? `${card.current_durability} / ${card.durability}` : card.durability}
                                </span>
                            </div>
                        )}
                    </div>
                    {showFlavor && cardFlavor && (
                        <div className="card-detail-flavor" style={{ marginTop: '20px', padding: '20px', background: 'rgba(0, 0, 0, 0.8)', border: '1px solid rgba(255, 215, 0, 0.3)', borderRadius: '8px' }}>
                            <p style={{ margin: 0, fontSize: '1rem', lineHeight: '1.8', color: '#ffd700', fontStyle: 'italic', fontWeight: '500' }}>{cardFlavor}</p>
                        </div>
                    )}
                    {!showFlavor && (
                        <div className="card-detail-description">
                            <h4>{getUILoc('LABELS.EFFECT')}:</h4>
                            <p>{cardDescription}</p>
                        </div>
                    )}

                    {relatedCards.length > 0 && (
                        <div className="related-cards-section">
                            <h4>{getUILoc('LABELS.RELATED_CARDS')}:</h4>
                            {relatedCards.map((rc, idx) => (
                                <div key={idx} className="related-card-item">
                                    <div className="related-card-header">
                                        <CardIllustration cardId={rc.id} isMini={true} />
                                        <div className="related-card-title-group">
                                            <span className="related-card-name">{rc.name}</span>
                                            <span className="related-card-type">{rc.typeName}</span>
                                            {rc.is_token && <span className="token-badge">TOKEN</span>}
                                        </div>
                                    </div>
                                    <p className="related-card-description">{rc.description}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CardDetail;
